import { Link } from '@tanstack/react-router';
import { Clock, Mail, ShieldCheck } from 'lucide-react';
import { ActionMenu, Button, Pill } from '@/shared/components';
import { formatDate } from '@/shared/lib/format';
import type { UserResponse } from '@/shared/api';
import {
  fullName,
  roleLabel,
  USER_STATUS_LABELS,
  USER_STATUS_PILL,
  userRole,
  userStatus,
} from './userStatus';
import { userRowActions, type UserRowHandlers } from './userRowActions';

interface Props extends UserRowHandlers {
  user: UserResponse;
  currentUserId?: string;
  selected: boolean;
  onToggleSelect: () => void;
  busy: boolean;
}

/**
 * Die Benutzerzeile als gestapelte Karte — die Mobilkomposition der Liste (§9).
 *
 * <p>Die Liste sitzt im Einstellungs-Layout und hat dort auch auf 1440 px nur rund 714 px. Acht
 * Spalten passen dort nicht; die Umschaltung hängt deshalb an einer Container-Query und nicht am
 * Fenster — dieselbe Lehre wie bei den System-Tabellen aus Block D.</p>
 */
export function UserCard({
  user: u,
  currentUserId,
  selected,
  onToggleSelect,
  busy,
  ...handlers
}: Props) {
  const status = userStatus(u);

  return (
    <li
      className={
        selected
          ? 'border-navy bg-card shadow-card ring-navy rounded-lg border p-4 ring-2 ring-inset'
          : 'border-line bg-card shadow-card rounded-lg border p-4'
      }
    >
      <div className="flex items-start gap-3">
        <label className="flex h-11 w-9 shrink-0 cursor-pointer items-center justify-center">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            aria-label={`${fullName(u)} auswählen`}
            className="accent-sealwax h-4 w-4 cursor-pointer"
          />
        </label>

        <div className="min-w-0 flex-1">
          <Link
            to="/users/$id"
            params={{ id: u.id }}
            className="text-ink hover:text-sealwax wrap-break-word font-serif text-lg underline-offset-4 hover:underline"
          >
            {fullName(u)}
          </Link>
          <p className="text-ink-soft wrap-break-word mt-0.5 flex items-start gap-1.5 text-sm">
            <Mail size={14} className="text-ink-faint mt-0.5 shrink-0" aria-hidden />
            {u.email}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Pill variant={USER_STATUS_PILL[status]} size="sm">
              {USER_STATUS_LABELS[status]}
            </Pill>
            <Pill variant="default" size="sm">
              {roleLabel(userRole(u))}
            </Pill>
          </div>

          <dl className="text-ink-soft mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div className="min-w-0">
              <dt className="text-ink-faint flex items-center gap-1 tracking-wide">
                <ShieldCheck size={12} aria-hidden />
                Zwei-Faktor
              </dt>
              <dd className="text-ink mt-0.5">{u.twoFactorEnabled ? 'aktiv' : 'fehlt'}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-ink-faint flex items-center gap-1 tracking-wide">
                <Clock size={12} aria-hidden />
                Zuletzt angemeldet
              </dt>
              <dd className="text-ink mt-0.5 tabular-nums">
                {u.lastLoginAt ? formatDate(u.lastLoginAt) : 'noch nie'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Der Rahmen des Menüs ist `inline-block`; ohne diese Klasse bliebe `w-full` an der
          Schaltfläche wirkungslos und die Trefferfläche schrumpfte auf die Textbreite. */}
      <ActionMenu
        className="mt-3 block w-full"
        trigger={
          <Button variant="ghost" className="border-line min-h-11 w-full border" loading={busy}>
            Aktionen
          </Button>
        }
        items={userRowActions(u, handlers, currentUserId)}
      />
    </li>
  );
}
